import { useCallback, useEffect, useRef, useState } from 'react';

type ScrollTarget = 'top' | 'bottom';

interface UseScrollContainerResult<T extends HTMLElement> {
  containerRef: React.RefObject<T>;
  scrollToTop: () => void;
  scrollToBottom: () => void;
}

/**
 * Smoothly scrolls a referenced container to the top or bottom after the next
 * render commit. Use when the trigger (e.g. adding an item) changes the
 * container's content in the same tick.
 */
export function useScrollContainer<T extends HTMLElement = HTMLDivElement>(): UseScrollContainerResult<T> {
  const containerRef = useRef<T>(null);
  const [pending, setPending] = useState<ScrollTarget | null>(null);

  useEffect(() => {
    if (!pending || !containerRef.current) return;
    const top = pending === 'top' ? 0 : containerRef.current.scrollHeight;
    containerRef.current.scrollTo({ top, behavior: 'smooth' });
    setPending(null);
  }, [pending]);

  const scrollToTop = useCallback(() => setPending('top'), []);
  const scrollToBottom = useCallback(() => setPending('bottom'), []);

  return { containerRef, scrollToTop, scrollToBottom };
}
